/**
 * File generation / processing only. No upload, no IPC.
 * Produces ProcessedFile[] for consumption by uploadQueue.
 */

import { imageUtils } from '@renderer/utils/imageUtils'
import { backgroundVideoProcessor } from '@renderer/utils/backgroundVideoProcessor'
import type { FrameConfig } from '@renderer/types/configState'
import type { PhotoState } from '@renderer/utils/generateFramedVideo'
import type {
  ProcessedFile,
  SaveFileMetadata,
  WatermarkConfig,
  GalleryItemForProcessing
} from '@renderer/types/outputPipeline.types'

const STRIP_NAME = (sessionId: string): string => `dreambooth-${sessionId}-strip.jpg`
const GIF_NAME = (sessionId: string): string => `dreambooth-${sessionId}-anim.gif`
const MP4_NAME = (sessionId: string): string => `dreambooth-${sessionId}-video.mp4`
const PHOTO_NAME = (sessionId: string, index: number): string =>
  `dreambooth-${sessionId}-photo-${index}.jpg`

/** Build metadata for a processed file (optional dimensions, etc.). */
function defaultMetadata(file: File): SaveFileMetadata {
  return { mimeType: file.type }
}

/**
 * Process strip blob into a single ProcessedFile (no upload).
 */
export function processStrip(stripBlob: Blob, sessionId: string): ProcessedFile {
  const fileName = STRIP_NAME(sessionId)
  const file = new File([stripBlob], fileName, { type: 'image/jpeg' })
  return {
    file,
    fileName,
    fileType: 'photos',
    uploadType: 'strip',
    metadata: defaultMetadata(file)
  }
}

/**
 * Create GIF and MP4 from photo URLs; returns both as ProcessedFile[] (no upload).
 */
export async function processGIF(
  photoUrls: string[],
  gifSpeed: number,
  sessionId: string,
  customGifFrame?: string
): Promise<ProcessedFile[]> {
  const [gifBlob, mp4Blob] = await Promise.all([
    imageUtils.createGIF(photoUrls, gifSpeed, customGifFrame),
    imageUtils.createMP4FromImages(photoUrls, gifSpeed, customGifFrame)
  ])
  const gifFile = new File([gifBlob], GIF_NAME(sessionId), { type: 'image/gif' })
  const mp4File = new File([mp4Blob], MP4_NAME(sessionId), { type: 'video/mp4' })
  return [
    { file: gifFile, fileName: gifFile.name, fileType: 'gifs', uploadType: 'gif', metadata: defaultMetadata(gifFile) },
    { file: mp4File, fileName: mp4File.name, fileType: 'videos', uploadType: 'gif', metadata: defaultMetadata(mp4File) }
  ]
}

/**
 * Process gallery items into ProcessedFile[] (one per photo). Applies watermark if config provided.
 */
export async function processIndividualPhotos(
  galleryItems: GalleryItemForProcessing[],
  sessionId: string,
  watermarkConfig?: WatermarkConfig
): Promise<ProcessedFile[]> {
  const items = galleryItems.filter((item) => item.photoUrl && item.photoUrl.trim() !== '')
  const results: ProcessedFile[] = []

  for (let index = 0; index < items.length; index++) {
    const item = items[index]
    let photoFile = await imageUtils.createFileFromUrl(
      item.photoUrl!,
      PHOTO_NAME(sessionId, index)
    )

    if (watermarkConfig?.watermarkUrl?.trim()) {
      try {
        const photoBlob = await imageUtils.applyWatermarkToBlob(
          photoFile,
          watermarkConfig.watermarkUrl,
          watermarkConfig.watermarkHorizontalAnchor,
          watermarkConfig.watermarkVerticalAnchor,
          watermarkConfig.watermarkHorizontalOffset,
          watermarkConfig.watermarkVerticalOffset
        )
        photoFile = new File([photoBlob], photoFile.name, { type: photoFile.type })
      } catch (error) {
        console.warn('[fileProcessor] applyWatermarkToBlob failed:', error)
      }
    }

    results.push({
      file: photoFile,
      fileName: photoFile.name,
      fileType: 'photos',
      uploadType: 'image',
      metadata: defaultMetadata(photoFile)
    })
  }

  return results
}

/** Params for delegating to background video processor (unchanged behavior). */
export interface ProcessVideoTaskParams {
  photosToProcess: string[]
  galleryItems: GalleryItemForProcessing[]
  frameConfig: FrameConfig
  photoStates: Record<string, PhotoState>
  recordingType: 'recording' | 'strip_live' | 'strip_story'
  recordingSpeed: number
  captureCountdown: number
  sessionId: string
  userId: string
  projectId: string
  galleryId: string
  onError?: (error: Error) => void
}

/**
 * Delegate to backgroundVideoProcessor; returns taskId. Video upload is handled inside the processor.
 */
export function processVideoTask(params: ProcessVideoTaskParams): string {
  const { photosToProcess, galleryItems, frameConfig, photoStates, recordingType, recordingSpeed, captureCountdown, sessionId, userId, projectId, galleryId, onError } = params
  if (!['recording', 'strip_live', 'strip_story'].includes(recordingType)) {
    throw new Error(`Invalid recordingType: ${recordingType}`)
  }

  const resources = photosToProcess.map((photoUrl) => {
    const item = galleryItems.find((i) => i.photoUrl === photoUrl)
    if (!item) throw new Error(`Could not find captured item for photo ${photoUrl}`)
    return {
      photoUrl: item.photoUrl!,
      videoUrl: item.recordingUrl || ''
    }
  })

  const taskId = `video-${recordingType}-${sessionId}-${Date.now()}`
  backgroundVideoProcessor.processVideo({
    id: taskId,
    type: recordingType === 'strip_story' ? 'story' : 'live',
    resources,
    frameConfig,
    sessionId,
    userId,
    projectId,
    galleryId,
    speed: recordingSpeed,
    fps: 30,
    photoStates,
    countdown: captureCountdown,
    onError: (error) => {
      if (onError) onError(error)
    }
  })

  return taskId
}
